import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, ChevronRight, Check, X, Edit2, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { getEnseigneDevisTypes, getDevisTypeDetails, getDevisTypeLignes, updateDevisTypeLigne, toggleLotInDevisType } from '../services/api';

const LOTS_MAAG = [
  { id: "1", nom: "PILOTAGE DES INTERVENANTS DIRECTS / BUREAU D'ETUDE" },
  { id: "2", nom: "GARDIENNAGE" },
  { id: "3", nom: "PALISSADE" },
  { id: "4", nom: "DEPOSE / DÉMOLITION" },
  { id: "5", nom: "MAÇONNERIE" },
  { id: "6", nom: "PROTECTION COUPE FEU" },
  { id: "7", nom: "VITRINE FACADE HABILLAGE" },
  { id: "8", nom: "SERRURERIE" },
  { id: "9", nom: "DOUBLAGE - CLOISONNEMENT - FAUX-PLAFOND" },
  { id: "10", nom: "PEINTURE" },
  { id: "11", nom: "REVETEMENT DE SOL" },
  { id: "12", nom: "ELECTRICITE" },
  { id: "13", nom: "CLIMATISATION - RAC - VMC" },
  { id: "14", nom: "PLOMBERIE SANITAIRES" },
  { id: "15", nom: "MENUISERIE - POSE MOBILIER" },
  { id: "16", nom: "SECURITE INCENDIE" },
  { id: "17", nom: "NETTOYAGE" },
  { id: "18", nom: "REMISE COMMERCIAL" }
];

export default function DevisTypesPage() {
  const { enseigneId } = useParams();
  const navigate = useNavigate();
  const [devisTypes, setDevisTypes] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [lignes, setLignes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingLignes, setLoadingLignes] = useState(false);
  const [expandedLots, setExpandedLots] = useState({});
  const [editingLigne, setEditingLigne] = useState(null);
  const [enseigneNom, setEnseigneNom] = useState('');

  useEffect(() => {
    loadDevisTypes();
  }, [enseigneId]);

  const loadDevisTypes = async () => {
    try {
      setLoading(true);
      const res = await getEnseigneDevisTypes(enseigneId);
      setDevisTypes(res.data);
      if (res.data.length > 0) {
        setEnseigneNom(res.data[0].enseigne_nom);
      }
    } catch (err) {
      toast.error('Erreur chargement des devis types');
    } finally {
      setLoading(false);
    }
  };

  const loadLignes = async (devisTypeId) => {
    try {
      setLoadingLignes(true);
      const res = await getDevisTypeLignes(devisTypeId);
      setLignes(res.data);
    } catch (err) {
      toast.error('Erreur chargement des lignes');
    } finally {
      setLoadingLignes(false);
    }
  };

  const handleSelectType = async (dt) => {
    setSelectedType(dt);
    await loadLignes(dt.id);
  };

  const toggleLot = (lotId) => {
    setExpandedLots(prev => ({ ...prev, [lotId]: !prev[lotId] }));
  };

  const handleToggleLotActif = async (lotId, currentlyActive) => {
    try {
      await toggleLotInDevisType(selectedType.id, lotId, !currentlyActive);
      toast.success(`Lot ${currentlyActive ? 'désactivé' : 'activé'}`);
      await loadLignes(selectedType.id);
      await loadDevisTypes();
    } catch (err) {
      toast.error('Erreur');
    }
  };

  const handleSaveLigne = async (ligne, applyToMaag = false) => {
    try {
      await updateDevisTypeLigne(selectedType.id, ligne.id, {
        prix_override: editingLigne.prix_override,
        descriptif_override: editingLigne.descriptif_override,
        quantite_defaut: editingLigne.quantite_defaut,
        apply_to_maag: applyToMaag
      });
      toast.success(applyToMaag ? 'Modifié et ajouté à la base MAAG' : `Modifié pour ${enseigneNom} uniquement`);
      setEditingLigne(null);
      await loadLignes(selectedType.id);
    } catch (err) {
      toast.error('Erreur');
    }
  };

  const getLignesByLot = (lotId) => {
    return lignes.filter(l => l.lot_id === lotId);
  };

  const isLotActive = (lotId) => {
    const lotLignes = getLignesByLot(lotId);
    return lotLignes.length > 0 && lotLignes.some(l => l.actif);
  };

  const formatMoney = (val) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(val || 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/enseignes')} className="p-2 hover:bg-slate-100 rounded-lg">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Devis Types - {enseigneNom}</h1>
          <p className="text-sm text-slate-500">4 modèles de devis personnalisables par enseigne</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Liste des 4 devis types */}
        <div className="space-y-3">
          <h2 className="font-semibold text-slate-700">Modèles disponibles</h2>
          {devisTypes.map(dt => (
            <button
              key={dt.id}
              onClick={() => handleSelectType(dt)}
              className={`w-full p-4 rounded-lg border text-left transition-all ${
                selectedType?.id === dt.id
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-slate-200 hover:border-slate-300 bg-white'
              }`}
            >
              <div className="flex items-center gap-3">
                <FileText className={`w-5 h-5 ${selectedType?.id === dt.id ? 'text-blue-600' : 'text-slate-400'}`} />
                <div className="flex-1">
                  <p className="font-medium text-slate-900">{dt.type_nom}</p>
                  <p className="text-xs text-slate-500">{dt.lignes_actives_count} / {dt.lignes_count} lignes actives</p>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-400" />
              </div>
            </button>
          ))}
        </div>

        {/* Détail du devis type sélectionné */}
        <div className="lg:col-span-3">
          {selectedType ? (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
              <div className="p-4 border-b border-slate-200">
                <h2 className="text-lg font-semibold text-slate-900">{selectedType.type_nom}</h2>
                <p className="text-sm text-slate-500">Activez/désactivez les lots et personnalisez les prix et descriptions</p>
              </div>

              {loadingLignes ? (
                <div className="flex items-center justify-center h-64">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                </div>
              ) : (
                <div className="divide-y divide-slate-100 max-h-[600px] overflow-y-auto">
                  {LOTS_MAAG.map(lot => {
                    const lotLignes = getLignesByLot(lot.id);
                    const lotActive = isLotActive(lot.id);
                    const activeLignes = lotLignes.filter(l => l.actif).length;

                    return (
                      <div key={lot.id} className={`${!lotActive ? 'opacity-50' : ''}`}>
                        {/* Lot header */}
                        <div 
                          className="flex items-center justify-between p-3 hover:bg-slate-50 cursor-pointer"
                          onClick={() => toggleLot(lot.id)}
                        >
                          <div className="flex items-center gap-3">
                            <button
                              onClick={(e) => { e.stopPropagation(); handleToggleLotActif(lot.id, lotActive); }}
                              className={`w-5 h-5 rounded flex items-center justify-center ${
                                lotActive ? 'bg-green-500 text-white' : 'bg-slate-200'
                              }`}
                            >
                              {lotActive && <Check className="w-3 h-3" />}
                            </button>
                            <span className="font-medium text-sm text-slate-700">
                              Lot {lot.id} - {lot.nom}
                            </span>
                            <span className="text-xs text-slate-400">
                              ({activeLignes}/{lotLignes.length})
                            </span>
                          </div>
                          <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${expandedLots[lot.id] ? 'rotate-90' : ''}`} />
                        </div>

                        {/* Lignes du lot */}
                        {expandedLots[lot.id] && lotLignes.length > 0 && (
                          <div className="bg-slate-50 px-4 py-2">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="text-slate-500">
                                  <th className="text-left py-1 w-8">Act.</th>
                                  <th className="text-left py-1">Désignation</th>
                                  <th className="text-left py-1 w-20">Unité</th>
                                  <th className="text-right py-1 w-16">Qté</th>
                                  <th className="text-right py-1 w-24">Prix</th>
                                  <th className="text-center py-1 w-16">Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {lotLignes.map(ligne => (
                                  <tr key={ligne.id} className={`border-t border-slate-200 ${!ligne.actif ? 'opacity-40' : ''}`}>
                                    <td className="py-2">
                                      <button
                                        onClick={() => updateDevisTypeLigne(selectedType.id, ligne.id, { actif: !ligne.actif }).then(() => loadLignes(selectedType.id))}
                                        className={`w-4 h-4 rounded flex items-center justify-center ${
                                          ligne.actif ? 'bg-green-500 text-white' : 'bg-slate-200'
                                        }`}
                                      >
                                        {ligne.actif && <Check className="w-2 h-2" />}
                                      </button>
                                    </td>
                                    <td className="py-2">
                                      <span className="font-medium">{ligne.prestation_designation}</span>
                                      {ligne.has_descriptif_override && (
                                        <span className="ml-1 text-amber-600 text-[10px]">●</span>
                                      )}
                                    </td>
                                    <td className="py-2 text-slate-500">{ligne.unite}</td>
                                    <td className="py-2 text-right">
                                      {editingLigne?.id === ligne.id ? (
                                        <input
                                          type="number"
                                          step="0.1"
                                          value={editingLigne.quantite_defaut}
                                          onChange={(e) => setEditingLigne({ ...editingLigne, quantite_defaut: parseFloat(e.target.value) })}
                                          className="w-14 px-1 py-0.5 border rounded text-right"
                                        />
                                      ) : (
                                        ligne.quantite_defaut
                                      )}
                                    </td>
                                    <td className="py-2 text-right">
                                      {editingLigne?.id === ligne.id ? (
                                        <input
                                          type="number"
                                          step="0.01"
                                          value={editingLigne.prix_override ?? ligne.prix_unitaire}
                                          onChange={(e) => setEditingLigne({ ...editingLigne, prix_override: parseFloat(e.target.value) })}
                                          className="w-20 px-1 py-0.5 border rounded text-right"
                                        />
                                      ) : (
                                        <span className={ligne.has_prix_override ? 'text-amber-600 font-medium' : ''}>
                                          {formatMoney(ligne.prix_unitaire)}
                                        </span>
                                      )}
                                    </td>
                                    <td className="py-2 text-center">
                                      {editingLigne?.id === ligne.id ? (
                                        <div className="flex items-center justify-center gap-1">
                                          <button
                                            onClick={() => handleSaveLigne(ligne, false)}
                                            className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                                            title={`${enseigneNom} uniquement`}
                                          >
                                            <Save className="w-3 h-3" />
                                          </button>
                                          <button
                                            onClick={() => handleSaveLigne(ligne, true)}
                                            className="p-1 text-green-600 hover:bg-green-100 rounded"
                                            title="Ajouter à la base MAAG"
                                          >
                                            <Check className="w-3 h-3" />
                                          </button>
                                          <button
                                            onClick={() => setEditingLigne(null)}
                                            className="p-1 text-slate-400 hover:bg-slate-100 rounded"
                                          >
                                            <X className="w-3 h-3" />
                                          </button>
                                        </div>
                                      ) : (
                                        <button
                                          onClick={() => setEditingLigne({ ...ligne, prix_override: ligne.prix_unitaire })}
                                          className="p-1 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                                        >
                                          <Edit2 className="w-3 h-3" />
                                        </button>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <div className="bg-slate-50 rounded-xl border border-dashed border-slate-300 h-64 flex items-center justify-center">
              <p className="text-slate-500">Sélectionnez un devis type pour le personnaliser</p>
            </div>
          )}
        </div>
      </div>

      {/* Légende */}
      <div className="flex items-center gap-6 text-xs text-slate-500">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-amber-500"></span>
          <span>Prix/description personnalisé pour cette enseigne</span>
        </div>
        <div className="flex items-center gap-2">
          <Save className="w-3 h-3 text-blue-600" />
          <span>Enregistrer pour {enseigneNom} uniquement</span>
        </div>
        <div className="flex items-center gap-2">
          <Check className="w-3 h-3 text-green-600" />
          <span>Enregistrer ET ajouter à la base MAAG</span>
        </div>
      </div>
    </div>
  );
}
